import { apiClient } from './apiClient';
import { unifiedCache } from '../utils/unifiedAnalysisCache';
import { isSlimCache, isIncompleteLighthouseCache, buildSlimLighthouseCache } from '../utils/analysisCacheHelpers';
import { LighthouseReport, LighthouseAnalysisResult } from '../types/lighthouse';

/**
 * Job queue response types for async Lighthouse analysis
 */
interface LighthouseAsyncJobResponse {
  success: boolean;
  data: {
    jobId: string;
    status: 'queued' | 'processing' | 'completed' | 'failed';
    url: string;
    formFactor: string;
    categories: string[];
    projectId: string | null;
    pollPath: string;
    pollInterval: number;
    message: string;
    queuedAt: string;
  };
}

interface JobStatusResponse {
  success: boolean;
  job: {
    id: string;
    type: string;
    status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'retrying';
    priority: number;
    data: any;
    result?: {
      success: boolean;
      data?: any;
      error?: string;
      metrics?: any;
    };
    progress?: {
      percentage: number;
      currentStep: string;
      message?: string;
    };
    attempts: number;
    maxAttempts: number;
    createdAt: string;
    startedAt?: string;
    completedAt?: string;
    failedAt?: string;
    error?: string;
  };
}

/**
 * Lighthouse Service
 * Frontend service for interacting with Lighthouse analysis API
 */
export class LighthouseService {
  private readonly baseUrl = '/lighthouse'; // 🔧 CRITICAL FIX: Use absolute path starting with / so apiClient adds /api prefix

  constructor() {
    // 🔍 LIGHTHOUSE DEBUG: Log service initialisation
    console.log('🔍 LIGHTHOUSE SERVICE INIT:', {
      baseUrl: this.baseUrl,
      fullApiPath: this.baseUrl,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Poll for job completion with progress updates
   * Returns the completed job result or throws an error on failure
   */
  private async pollJobUntilComplete(
    jobId: string,
    options: {
      pollInterval?: number;
      maxAttempts?: number;
      onProgress?: (progress: { percentage: number; currentStep: string; message?: string }) => void;
    } = {}
  ): Promise<LighthouseAnalysisResult> {
    const pollInterval = options.pollInterval || 3000;
    const maxAttempts = options.maxAttempts || 100; // ~5 minutes with 3s polling
    let attempts = 0;

    console.log('🔄 LIGHTHOUSE JOB POLLING: Starting poll loop', {
      jobId,
      pollInterval,
      maxAttempts,
      timestamp: new Date().toISOString()
    });

    while (attempts < maxAttempts) {
      attempts++;

      try {
        const response = await apiClient.get<JobStatusResponse>(
          `/jobs/${jobId}`,
          { timeout: 10000 }
        ) as unknown as JobStatusResponse;

        // Backend returns { success, job } at top level — NOT wrapped in .data
        // (apiClient.transformResponse just returns the JSON body as-is)
        if (!response.success || !response.job) {
          console.warn('⚠️ LIGHTHOUSE JOB POLLING: Failed to get job status', {
            jobId,
            attempt: attempts,
            response
          });
          await this.delay(pollInterval);
          continue;
        }

        const job = response.job;

        console.log('🔍 LIGHTHOUSE JOB POLLING: Status update', {
          jobId,
          status: job.status,
          progress: job.progress,
          attempt: attempts,
          timestamp: new Date().toISOString()
        });

        // Report progress if callback provided
        if (options.onProgress && job.progress) {
          options.onProgress(job.progress);
        }

        // Check terminal states
        if (job.status === 'completed') {
          if (job.result?.success && job.result?.data) {
            console.log('✅ LIGHTHOUSE JOB POLLING: Job completed successfully', {
              jobId,
              attempts,
              processingTime: job.completedAt ?
                new Date(job.completedAt).getTime() - new Date(job.createdAt).getTime() : 'unknown'
            });

            return job.result.data as LighthouseAnalysisResult;
          } else {
            throw new Error(job.result?.error || 'Lighthouse analysis completed but no data returned');
          }
        }

        if (job.status === 'failed') {
          console.error('❌ LIGHTHOUSE JOB POLLING: Job failed', {
            jobId,
            error: job.error,
            attempts: job.attempts,
            maxAttempts: job.maxAttempts
          });
          throw new Error(job.error || 'Lighthouse analysis failed');
        }

        if (job.status === 'cancelled') {
          throw new Error('Lighthouse analysis was cancelled');
        }

        // Continue polling for pending/processing/retrying states
        await this.delay(pollInterval);

      } catch (error) {
        // Don't retry on thrown errors from job completion/failure
        if (error instanceof Error &&
            (error.message.includes('failed') ||
             error.message.includes('cancelled') ||
             error.message.includes('no data'))) {
          throw error;
        }

        console.warn('⚠️ LIGHTHOUSE JOB POLLING: Error during poll attempt', {
          jobId,
          attempt: attempts,
          error: error instanceof Error ? error.message : 'Unknown error'
        });

        await this.delay(pollInterval);
      }
    }

    throw new Error(`Lighthouse analysis timed out after ${maxAttempts} poll attempts`);
  }

  /**
   * Helper method for async delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Validate lighthouse data before caching to prevent infinite loops
   * CRITICAL: Only cache data with valid analysis_id and actual lighthouse reports
   */
  private validateLighthouseDataForCaching(data: any): {
    isValid: boolean;
    analysisId: string | null;
    hasMobileData: boolean;
    hasDesktopData: boolean;
    issues: string[];
  } {
    const issues: string[] = [];
    
    // Check if data exists
    if (!data) {
      issues.push('response_data_is_null_or_undefined');
      return {
        isValid: false,
        analysisId: null,
        hasMobileData: false,
        hasDesktopData: false,
        issues
      };
    }
    
    // Check analysis_id - it can be at root level OR inside mobile/desktop objects
    let analysisId = data.analysis_id;
    if (!analysisId && data.mobile?.analysis_id) {
      analysisId = data.mobile.analysis_id;
    }
    if (!analysisId && data.desktop?.analysis_id) {
      analysisId = data.desktop.analysis_id;
    }
    
    if (!analysisId || analysisId === undefined || analysisId === null || analysisId === '') {
      issues.push('analysis_id_is_missing_undefined_or_empty');
    }
    
    // Check mobile data - look for lighthouse categories OR lighthouse scores
    const hasMobileData = data.mobile && (
      (data.mobile.categories && Object.keys(data.mobile.categories).length > 0) ||
      (data.mobile.performance_score !== undefined && data.mobile.performance_score !== null)
    );
    
    // Check desktop data - look for lighthouse categories OR lighthouse scores
    const hasDesktopData = data.desktop && (
      (data.desktop.categories && Object.keys(data.desktop.categories).length > 0) ||
      (data.desktop.performance_score !== undefined && data.desktop.performance_score !== null)
    );
    
    // Must have at least one valid lighthouse report
    if (!hasMobileData && !hasDesktopData) {
      issues.push('no_valid_mobile_or_desktop_lighthouse_data');
    }
    
    const isValid = issues.length === 0;
    
    return {
      isValid,
      analysisId: analysisId || null,
      hasMobileData,
      hasDesktopData,
      issues
    };
  }

  /**
   * Normalise URL for consistent caching
   * CRITICAL: This must match the URL normalisation used in other parts of the app
   */
  private normalizeUrl(url: string): string {
    // Remove protocol and www prefix for consistent caching
    let normalized = url.replace(/^https?:\/\//, '').replace(/^www\./, '');
    
    // Remove trailing slash for consistency
    normalized = normalized.replace(/\/$/, '');
    
    // Always add https:// protocol for final URL
    const finalUrl = `https://${normalized}`;
    
    console.log('🔧 LIGHTHOUSE URL NORMALIZATION:', {
      original: url,
      intermediate: normalized,
      final: finalUrl,
      timestamp: new Date().toISOString()
    });
    
    return finalUrl;
  }

  /**
   * Check for existing cached lighthouse analysis
   * ENHANCED: Checks unified cache first, then database fallback
   */
  async getExistingAnalysis(url: string, options: {
    projectId?: string;
    maxAge?: number; // Max age in milliseconds, defaults to 24 hours
  } = {}): Promise<LighthouseAnalysisResult | null> {
    if (!options.projectId) {
      console.log('ℹ️ LIGHTHOUSE CACHE: No projectId provided for cache lookup');
      return null;
    }

    const maxAge = options.maxAge || 24 * 60 * 60 * 1000; // 24 hours default
    const normalizedUrl = this.normalizeUrl(url);

    try {
      console.log('🔍 LIGHTHOUSE CACHE: Checking for existing analysis', {
        originalUrl: url,
        normalizedUrl,
        projectId: options.projectId,
        maxAge: `${Math.round(maxAge / 1000 / 60)}min`,
        timestamp: new Date().toISOString()
      });

      const cachedData = await unifiedCache.get<{
        mobile: any;
        desktop: any;
        analysis_id: string;
        url: string;
        created_at: string;
      }>('lighthouse', options.projectId, normalizedUrl);

      if (cachedData) {
        // Check if cache is still fresh
        const createdAtTime = cachedData.created_at ? new Date(cachedData.created_at).getTime() : 0;
        const cacheAge = Date.now() - createdAtTime;
        
        // Handle invalid/missing created_at - treat as stale
        if (!cachedData.created_at || isNaN(createdAtTime)) {
          console.log('🗑️ LIGHTHOUSE CACHE: Invalid cache timestamp, treating as stale', {
            created_at: cachedData.created_at,
            createdAtTime,
            timestamp: new Date().toISOString()
          });
          
          await unifiedCache.invalidate('lighthouse', options.projectId!, normalizedUrl);
          return null;
        }
        
        // Validate that cached data actually contains lighthouse reports - check for categories OR scores
        const hasMobileData = cachedData.mobile && (
          (cachedData.mobile.categories && Object.keys(cachedData.mobile.categories).length > 0) ||
          (cachedData.mobile.performance_score !== undefined && cachedData.mobile.performance_score !== null)
        );
        
        const hasDesktopData = cachedData.desktop && (
          (cachedData.desktop.categories && Object.keys(cachedData.desktop.categories).length > 0) ||
          (cachedData.desktop.performance_score !== undefined && cachedData.desktop.performance_score !== null)
        );

        if (!(hasMobileData || hasDesktopData)) {
          console.log('🗑️ LIGHTHOUSE CACHE: Cached data contains no actual lighthouse reports, invalidating', {
            analysisId: cachedData.analysis_id,
            hasMobileData,
            hasDesktopData,
            note: 'Empty cache data from incomplete backend processing'
          });
          
          // Remove empty cache data
          console.log('🗑️ LIGHTHOUSE CACHE INVALIDATION: Removing empty cache data', {
            reason: 'empty_mobile_desktop_data',
            projectId: options.projectId,
            url: normalizedUrl,
            analysisId: cachedData.analysis_id
          });
          await unifiedCache.invalidate('lighthouse', options.projectId, normalizedUrl);
          return null;
        }
        
        if (cacheAge <= maxAge) {
          console.log('✅ LIGHTHOUSE CACHE HIT: Found fresh cached analysis with actual data', {
            analysisId: cachedData.analysis_id,
            ageMinutes: Math.round(cacheAge / 1000 / 60),
            maxAgeMinutes: Math.round(maxAge / 1000 / 60),
            hasMobile: !!cachedData.mobile,
            hasDesktop: !!cachedData.desktop,
            hasMobileData,
            hasDesktopData,
            timestamp: new Date().toISOString()
          });

          // Return in the same format as analyzeUrl
          return {
            analysis_id: cachedData.analysis_id,
            mobile: cachedData.mobile,
            desktop: cachedData.desktop,
            success: true,
            timestamp: cachedData.created_at,
            cache_source: 'localStorage'
          } as LighthouseAnalysisResult;
        } else {
          console.log('⏰ LIGHTHOUSE CACHE: Cached analysis too old, invalidating', {
            ageMinutes: Math.round(cacheAge / 1000 / 60),
            maxAgeMinutes: Math.round(maxAge / 1000 / 60),
            timestamp: new Date().toISOString()
          });
          
          // Remove stale cache
          console.log('🗑️ LIGHTHOUSE CACHE INVALIDATION: Removing stale cache data', {
            reason: 'cache_too_old',
            ageMinutes: Math.round(cacheAge / 1000 / 60),
            maxAgeMinutes: Math.round(maxAge / 1000 / 60),
            projectId: options.projectId,
            url: normalizedUrl
          });
          await unifiedCache.invalidate('lighthouse', options.projectId, normalizedUrl);
        }
      } else {
        console.log('🔍 LIGHTHOUSE CACHE MISS: No cached analysis found');
      }

      return null;
    } catch (error) {
      console.warn('⚠️ LIGHTHOUSE CACHE: Error checking cache (non-critical)', {
        error: error instanceof Error ? error.message : 'Unknown error',
        url,
        projectId: options.projectId
      });
      return null;
    }
  }

  /**
   * Run Lighthouse analysis on a URL
   * ENHANCED: Now checks cache first, then runs analysis if needed
   */
  async analyzeUrl(url: string, options: {
    formFactor?: 'mobile' | 'desktop';
    categories?: string[];
    timeout?: number;
    projectId?: string;
    forceRefresh?: boolean;
  } = {}): Promise<LighthouseAnalysisResult> {
    const fullUrl = `${this.baseUrl}/analyze`;
    const normalizedUrl = this.normalizeUrl(url);
    
    // 🔍 LIGHTHOUSE DEBUG: Log API call details
    console.log('🔍 LIGHTHOUSE ANALYZE CALL (UNIFIED CACHE):', {
      baseUrl: this.baseUrl,
      fullUrl,
      originalUrl: url,
      normalizedUrl,
      options,
      cacheStrategy: 'check_cache_first_then_analyze_if_needed',
      timestamp: new Date().toISOString()
    });

    // 🚀 UNIFIED CACHE: Check for existing cached analysis first (unless forceRefresh)
    if (!options.forceRefresh && options.projectId) {
      console.log('🔍 LIGHTHOUSE: Checking for existing cached analysis before running new analysis');
      
      try {
        const existingAnalysis = await this.getExistingAnalysis(normalizedUrl, {
          projectId: options.projectId,
          maxAge: 24 * 60 * 60 * 1000 // 24 hours
        });

        if (existingAnalysis) {
          console.log('✅ LIGHTHOUSE: Using existing cached analysis (skipping new analysis)', {
            analysisId: existingAnalysis.analysis_id,
            cacheSource: existingAnalysis.cache_source,
            timestamp: new Date().toISOString()
          });
          return existingAnalysis;
        }
        
        console.log('🔍 LIGHTHOUSE: No cached analysis found, proceeding with new analysis');
      } catch (cacheError) {
        console.warn('⚠️ LIGHTHOUSE: Cache check failed, proceeding with new analysis', {
          error: cacheError instanceof Error ? cacheError.message : 'Unknown error'
        });
      }
    }

    // 🚀 UNIFIED CACHE: If forceRefresh is true, invalidate existing cache
    if (options.forceRefresh && options.projectId) {
      console.log('🗑️ UNIFIED CACHE: Invalidating existing lighthouse analysis cache due to forceRefresh', {
        reason: 'forceRefresh_flag',
        projectId: options.projectId,
        url: normalizedUrl
      });
      try {
        await unifiedCache.invalidate('lighthouse', options.projectId, normalizedUrl);
      } catch (cacheError) {
        console.warn('⚠️ UNIFIED CACHE: Failed to invalidate lighthouse cache (non-critical)', {
          error: cacheError instanceof Error ? cacheError.message : 'Unknown error'
        });
      }
    }
    
    // 🔄 ASYNC JOB QUEUE: Submit job to async endpoint and poll for results
    const asyncUrl = `${this.baseUrl}/analyze-async`;

    console.log('🔄 LIGHTHOUSE ASYNC: Submitting job to async endpoint', {
      asyncUrl,
      url: normalizedUrl,
      options,
      timestamp: new Date().toISOString()
    });

    const asyncResponse = await apiClient.post<LighthouseAsyncJobResponse['data']>(
      asyncUrl,
      {
        url: normalizedUrl, // Use normalised URL for API request consistency
        ...options
      },
      {
        timeout: 30000, // 30 seconds to submit job (job itself runs asynchronously)
        performanceTracking: true
      }
    );

    if (!asyncResponse.success || !asyncResponse.data) {
      // Fallback to sync endpoint if async fails
      console.warn('⚠️ LIGHTHOUSE ASYNC: Failed to submit async job, falling back to sync endpoint', {
        error: asyncResponse.error,
        timestamp: new Date().toISOString()
      });

      const syncResponse = await apiClient.post<LighthouseAnalysisResult>(
        fullUrl,
        {
          url: normalizedUrl,
          ...options
        },
        {
          timeout: options.timeout || 90000, // 90 seconds for Lighthouse analysis
          performanceTracking: true
        }
      );

      if (!syncResponse.success || !syncResponse.data) {
        throw new Error(syncResponse.error || 'Failed to analyse URL with Lighthouse');
      }

      // Return sync response directly
      const response = syncResponse;
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to analyse URL with Lighthouse');
      }

      // Continue with caching logic using syncResponse.data
      return this.cacheAndReturnResult(normalizedUrl, response.data, options);
    }

    // Extract job info from async response
    const { jobId, pollInterval } = asyncResponse.data;

    console.log('✅ LIGHTHOUSE ASYNC: Job submitted successfully', {
      jobId,
      pollInterval,
      pollPath: asyncResponse.data.pollPath,
      queuedAt: asyncResponse.data.queuedAt,
      timestamp: new Date().toISOString()
    });

    // Poll for job completion
    const result = await this.pollJobUntilComplete(jobId, {
      pollInterval,
      maxAttempts: 100, // ~5 minutes with 3s polling
      onProgress: (progress) => {
        console.log('🔄 LIGHTHOUSE PROGRESS:', {
          jobId,
          percentage: progress.percentage,
          currentStep: progress.currentStep,
          message: progress.message
        });
      }
    });

    // Cache and return the result
    return this.cacheAndReturnResult(normalizedUrl, result, options);
  }

  /**
   * Helper method to cache lighthouse results and return them
   * Extracted to avoid code duplication between sync and async paths
   */
  private async cacheAndReturnResult(
    normalizedUrl: string,
    responseData: LighthouseAnalysisResult,
    options: { projectId?: string }
  ): Promise<LighthouseAnalysisResult> {
    const response = { success: true, data: responseData };

    // 🚀 UNIFIED CACHE: Store the new lighthouse analysis result in cache ONLY if we have valid, complete data
    if (options.projectId && response.data) {
      const validation = this.validateLighthouseDataForCaching(response.data);

      if (validation.isValid) {
        try {
          console.log('💾 UNIFIED CACHE: Storing SLIM lighthouse analysis in localStorage');

          // 🚀 SLIM CACHE: Use helper to build slim representation (~2-5KB vs 400KB+ full)
          const slimLighthouseCache = buildSlimLighthouseCache({
            normalizedUrl,
            data: response.data,
            analysisId: validation.analysisId || undefined
          });

          await unifiedCache.set('lighthouse', options.projectId, normalizedUrl, slimLighthouseCache, {
            ttl: 24 * 60 * 60 * 1000, // 24 hours
            syncWithDatabase: true
          });

          console.log('✅ UNIFIED CACHE: SLIM Lighthouse analysis cached successfully', {
            projectId: options.projectId,
            normalizedUrl: normalizedUrl,
            analysisId: validation.analysisId,
            hasMobile: !!slimLighthouseCache.mobile,
            hasDesktop: !!slimLighthouseCache.desktop,
            cacheSize: 'slim (~2-5KB)',
            cacheStrategy: 'slim_cache_for_quota_safety',
            timestamp: new Date().toISOString()
          });
        } catch (cacheError) {
          console.warn('⚠️ UNIFIED CACHE: Failed to cache lighthouse analysis (non-critical)', {
            error: cacheError instanceof Error ? cacheError.message : 'Unknown error',
            projectId: options.projectId,
            normalizedUrl: normalizedUrl,
            analysisId: validation.analysisId
          });
        }
      } else {
        console.log('⏭️ UNIFIED CACHE: Skipping lighthouse cache storage - data validation failed', {
          projectId: options.projectId,
          normalizedUrl: normalizedUrl,
          analysisId: validation.analysisId,
          hasMobileData: validation.hasMobileData,
          hasDesktopData: validation.hasDesktopData,
          validationIssues: validation.issues,
          note: 'Preventing infinite loop by not caching incomplete data'
        });
      }
    } else {
      console.log('ℹ️ UNIFIED CACHE: Skipping lighthouse cache storage - missing projectId or response data', {
        hasProjectId: !!options.projectId,
        hasAnalysisData: !!response.data,
        normalizedUrl
      });
    }

    return response.data;
  }

  /**
   * Get existing Lighthouse report by analysis ID
   * Returns both mobile and desktop reports
   * ENHANCED: Now uses unified cache for localStorage-first performance
   */
  async getReport(analysisId: string, options: { refresh?: boolean; projectId?: string; cacheOnly?: boolean; url?: string } = {}): Promise<{mobile: LighthouseReport, desktop: LighthouseReport, cache_info?: any}> {
    const normalizedUrl = options.url ? this.normalizeUrl(options.url) : options.url;
    
    console.log('🔍 LIGHTHOUSE GET REPORT: Starting with unified cache integration', {
      analysisId,
      options,
      originalUrl: options.url,
      normalizedUrl,
      cacheStrategy: 'localStorage_first_with_database_fallback',
      timestamp: new Date().toISOString()
    });

    // 🚀 UNIFIED CACHE: Try cache first if we have project context and not forcing refresh
    if (!options.refresh && options.projectId && normalizedUrl) {
      console.log('🔍 UNIFIED CACHE: Checking localStorage for lighthouse report');
      
      try {
        const cachedReport = await unifiedCache.get<{mobile: LighthouseReport, desktop: LighthouseReport}>('lighthouse', options.projectId, normalizedUrl);
        
        if (cachedReport) {
          // 🚀 SLIM CACHE BYPASS: Don't return slim or incomplete cache - treat as miss
          if (isSlimCache(cachedReport) || isIncompleteLighthouseCache(cachedReport)) {
            console.log('⚠️ UNIFIED CACHE: Slim/incomplete lighthouse cache found - treating as miss, will fetch full data', {
              isSlim: isSlimCache(cachedReport),
              isIncomplete: isIncompleteLighthouseCache(cachedReport),
              analysisId,
              projectId: options.projectId
            });
            // Fall through to database/API lookup
          } else {
            // Guard against poisoned/partial cached data (common failure: mobile perf = 0 while desktop is valid)
            const mobilePerf = (cachedReport as any).mobile?.performance_score;
            const desktopPerf = (cachedReport as any).desktop?.performance_score;
            const hasNumericScores = typeof mobilePerf === 'number' && typeof desktopPerf === 'number';
            const looksCorrupt =
              hasNumericScores &&
              (
                (mobilePerf === 0 && desktopPerf > 0) ||
                (desktopPerf === 0 && mobilePerf > 0) ||
                (mobilePerf === 0 && desktopPerf === 0)
              );

            if (looksCorrupt) {
            console.warn('🚨 UNIFIED CACHE INVALIDATION: Cached lighthouse report looks corrupted (zero score detected), invalidating localStorage cache', {
              analysisId,
              projectId: options.projectId,
              normalizedUrl,
              mobilePerf,
              desktopPerf,
              source: 'localStorage_cache',
              timestamp: new Date().toISOString()
            });

            await unifiedCache.invalidate('lighthouse', options.projectId, normalizedUrl);
          } else {
          console.log('✅ UNIFIED CACHE HIT: Found lighthouse report in localStorage', {
            analysisId,
            projectId: options.projectId,
            originalUrl: options.url,
            normalizedUrl,
            hasMobile: !!cachedReport.mobile,
            hasDesktop: !!cachedReport.desktop,
            mobileScore: cachedReport.mobile?.categories?.performance?.score,
            desktopScore: cachedReport.desktop?.categories?.performance?.score,
            source: 'localStorage_cache',
            timestamp: new Date().toISOString()
          });
          
          return {
            ...cachedReport,
            cache_info: {
              source: 'localStorage',
              cached_at: new Date().toISOString()
            }
          };
            }
          }
        }

        console.log('🔍 UNIFIED CACHE MISS: No cached lighthouse report found, checking database/API');
      } catch (cacheError) {
        console.warn('⚠️ UNIFIED CACHE: Error checking cache (non-critical)', {
          error: cacheError instanceof Error ? cacheError.message : 'Unknown error',
          analysisId,
          projectId: options.projectId,
          url: options.url
        });
      }
    } else {
      console.log('🔍 UNIFIED CACHE: Skipping cache check', {
        reason: options.refresh ? 'refresh_requested' : 'missing_project_context',
        hasProjectId: !!options.projectId,
        hasUrl: !!options.url,
        refresh: options.refresh
      });
    }

    // 🔧 CRITICAL FIX: Use cached-only endpoint when cacheOnly is true
    if (options.cacheOnly === true) {
      console.log('🔍 LIGHTHOUSE GET REPORT: Using cache-only mode (NO API CALLS):', {
        analysisId,
        cacheOnly: options.cacheOnly,
        note: 'Will query database directly - NO external API calls',
        timestamp: new Date().toISOString()
      });
      
      const cachedUrl = `${this.baseUrl}/reports/${analysisId}/cached`;
      
      console.log('🔍 LIGHTHOUSE GET REPORT: Database-only query', {
        baseUrl: this.baseUrl,
        cachedUrl,
        analysisId,
        note: 'This should NEVER make external API calls - database only',
        timestamp: new Date().toISOString()
      });
      
      try {
        const response = await apiClient.get<{mobile: LighthouseReport, desktop: LighthouseReport}>(
          cachedUrl,
          {
            cache: false, // Don't use HTTP cache, go directly to database
            timeout: 5000 // Short timeout since this is database-only
          }
        );

        console.log('🔍 LIGHTHOUSE GET REPORT: Database response received', {
          success: response?.success,
          hasData: !!response?.data,
          timestamp: new Date().toISOString()
        });

        if (!response.success || !response.data) {
          console.log('🔍 LIGHTHOUSE GET REPORT: No cached data in database', {
            success: response?.success,
            hasData: !!response?.data,
            error: response?.error,
            timestamp: new Date().toISOString()
          });
          throw new Error('No cached lighthouse data available for this analysis');
        }

        console.log('✅ LIGHTHOUSE GET REPORT: Successfully loaded from database cache', {
          hasMobile: !!response.data.mobile,
          hasDesktop: !!response.data.desktop,
          timestamp: new Date().toISOString()
        });

        // 🚀 UNIFIED CACHE: Store database result in localStorage for next time (with validation)
        if (options.projectId && normalizedUrl) {
          try {
            const validation = this.validateLighthouseDataForCaching(response.data);

            if (validation.isValid) {
              console.log('💾 UNIFIED CACHE: Storing SLIM database lighthouse report in localStorage');

              // 🚀 SLIM CACHE: Use helper to build slim representation
              const slimCacheData = buildSlimLighthouseCache({
                normalizedUrl: normalizedUrl!,
                data: response.data,
                analysisId: validation.analysisId || undefined
              });

              await unifiedCache.set('lighthouse', options.projectId, normalizedUrl, slimCacheData, {
                ttl: 24 * 60 * 60 * 1000, // 24 hours
                syncWithDatabase: true
              });

              console.log('✅ UNIFIED CACHE: SLIM database lighthouse report cached successfully', {
                analysisId: validation.analysisId,
                cacheSize: 'slim (~2-5KB)',
                hasMobileData: validation.hasMobileData,
                hasDesktopData: validation.hasDesktopData
              });
            } else {
              console.log('⏭️ UNIFIED CACHE: Skipping database lighthouse cache - validation failed', {
                validationIssues: validation.issues,
                analysisId: validation.analysisId,
                note: 'Preventing infinite loop by not caching incomplete database data'
              });
            }
          } catch (cacheError) {
            console.warn('⚠️ UNIFIED CACHE: Failed to cache database lighthouse report (non-critical)', {
              error: cacheError instanceof Error ? cacheError.message : 'Unknown error'
            });
          }
        }

        return response.data;
      } catch (error: any) {
        console.log('🔍 LIGHTHOUSE GET REPORT: Database cache miss or error', {
          error,
          errorMessage: error?.message,
          errorStatus: error?.response?.status,
          analysisId,
          cachedUrl,
          note: 'This is expected if no lighthouse data exists in database yet',
          timestamp: new Date().toISOString()
        });
        throw new Error('No cached lighthouse data available for this analysis');
      }
    }
    
    const fullUrl = `${this.baseUrl}/reports/${analysisId}`;
    
    // 🔍 LIGHTHOUSE DEBUG: Log API call details with project context
    console.log('🔍 LIGHTHOUSE GET REPORT CALL:', {
      baseUrl: this.baseUrl,
      fullUrl,
      analysisId,
      refresh: options.refresh,
      projectId: options.projectId,
      cacheOnly: options.cacheOnly,
      note: 'Will make API calls since cacheOnly is not true',
      timestamp: new Date().toISOString()
    });
    
    // Add project context to params for project-aware cache busting
    const params: any = {};
    if (options.refresh) {
      params.refresh = 'true';
    }
    if (options.projectId) {
      params.projectId = options.projectId;
    }
    
    // 🔧 CRITICAL FIX: Proper cache strategy based on options
    const shouldUseCache = !options.refresh && options.cacheOnly !== false;
    const cacheConfig = shouldUseCache ? {
      cache: true,
      cacheTTL: 5 * 60 * 1000 // Cache for 5 minutes
    } : {
      cache: false,
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    };
    
    console.log('🔍 LIGHTHOUSE GET REPORT: Making API request', {
      fullUrl,
      params,
      shouldUseCache,
      cacheConfig,
      optionsRefresh: options.refresh,
      optionsCacheOnly: options.cacheOnly,
      timestamp: new Date().toISOString()
    });
    
    try {
      const response = await apiClient.get<{mobile: LighthouseReport, desktop: LighthouseReport, cache_info?: any}>(
        fullUrl,
        {
          params,
          ...cacheConfig
        }
      );

      console.log('🔍 LIGHTHOUSE GET REPORT: API response received', {
        success: response?.success,
        hasData: !!response?.data,
        responseKeys: response ? Object.keys(response) : 'null',
        dataKeys: response?.data ? Object.keys(response.data) : 'null',
        error: response?.error,
        timestamp: new Date().toISOString(),
        // 🔍 DIAGNOSTIC: Log detailed response structure
        responseStructure: {
          responseType: typeof response,
          responseIsNull: response === null,
          responseIsUndefined: response === undefined,
          dataType: typeof response?.data,
          dataIsNull: response?.data === null,
          dataIsUndefined: response?.data === undefined,
          fullResponsePreview: response ? JSON.stringify(response).substring(0, 500) : 'null'
        }
      });

      if (!response.success || !response.data) {
        console.error('❌ LIGHTHOUSE GET REPORT: Response indicates failure', {
          success: response?.success,
          hasData: !!response?.data,
          error: response?.error,
          fullResponse: response,
          timestamp: new Date().toISOString(),
          // 🔍 DIAGNOSTIC: Enhanced error logging
          diagnosticInfo: {
            responseExists: !!response,
            successField: response?.success,
            successType: typeof response?.success,
            dataField: response?.data,
            dataType: typeof response?.data,
            errorField: response?.error,
            messageField: response?.message,
            allResponseKeys: response ? Object.keys(response) : 'no response object'
          }
        });
        throw new Error(response?.error || response?.message || 'Failed to fetch Lighthouse report');
      }

      console.log('✅ LIGHTHOUSE GET REPORT: Success', {
        hasMobile: !!response.data.mobile,
        hasDesktop: !!response.data.desktop,
        hasCacheInfo: !!response.data.cache_info,
        timestamp: new Date().toISOString()
      });

      // 🚀 UNIFIED CACHE: Store API result in localStorage for next time (with validation)
      if (options.projectId && normalizedUrl) {
        try {
          const validation = this.validateLighthouseDataForCaching(response.data);

          if (validation.isValid) {
            console.log('💾 UNIFIED CACHE: Storing SLIM API lighthouse report in localStorage');

            // 🚀 SLIM CACHE: Use helper to build slim representation
            const slimCacheData = buildSlimLighthouseCache({
              normalizedUrl: normalizedUrl!,
              data: response.data,
              analysisId: validation.analysisId || undefined
            });

            await unifiedCache.set('lighthouse', options.projectId, normalizedUrl, slimCacheData, {
              ttl: 24 * 60 * 60 * 1000, // 24 hours
              syncWithDatabase: true
            });

            console.log('✅ UNIFIED CACHE: SLIM API lighthouse report cached successfully', {
              projectId: options.projectId,
              originalUrl: options.url,
              normalizedUrl,
              analysisId: validation.analysisId,
              cacheSize: 'slim (~2-5KB)',
              hasMobileData: validation.hasMobileData,
              hasDesktopData: validation.hasDesktopData,
              cacheStrategy: 'slim_api_result_cached',
              timestamp: new Date().toISOString()
            });
          } else {
            console.log('⏭️ UNIFIED CACHE: Skipping API lighthouse cache - validation failed', {
              validationIssues: validation.issues,
              analysisId: validation.analysisId,
              hasMobileData: validation.hasMobileData,
              hasDesktopData: validation.hasDesktopData,
              note: 'Preventing infinite loop by not caching incomplete API data'
            });
          }
        } catch (cacheError) {
          console.warn('⚠️ UNIFIED CACHE: Failed to cache API lighthouse report (non-critical)', {
            error: cacheError instanceof Error ? cacheError.message : 'Unknown error',
            projectId: options.projectId,
            originalUrl: options.url,
            normalizedUrl,
            analysisId
          });
        }
      } else {
        console.log('ℹ️ UNIFIED CACHE: Skipping lighthouse cache storage - missing project context', {
          hasProjectId: !!options.projectId,
          hasUrl: !!options.url,
          originalUrl: options.url,
          normalizedUrl,
          analysisId
        });
      }

      return response.data;
    } catch (error: any) {
      console.error('❌ LIGHTHOUSE GET REPORT: Exception caught', {
        error,
        errorMessage: error?.message,
        errorStatus: error?.response?.status,
        errorData: error?.response?.data,
        analysisId,
        fullUrl,
        params,
        timestamp: new Date().toISOString()
      });
      throw error;
    }
  }


  /**
   * Get Lighthouse reports for a project
   */
  async getProjectReports(projectId: string, options: {
    limit?: number;
    offset?: number;
    sortBy?: 'created_at' | 'performance_score';
    sortOrder?: 'asc' | 'desc';
  } = {}): Promise<{
    reports: LighthouseReport[];
    total: number;
    hasMore: boolean;
  }> {
    const response = await apiClient.get<{
      reports: LighthouseReport[];
      total: number;
      hasMore: boolean;
    }>(`${this.baseUrl}/projects/${projectId}/reports`, {
      params: {
        limit: options.limit || 10,
        offset: options.offset || 0,
        sort_by: options.sortBy || 'created_at',
        sort_order: options.sortOrder || 'desc'
      },
      cache: true,
      cacheTTL: 2 * 60 * 1000 // Cache for 2 minutes
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch project Lighthouse reports');
    }

    return response.data;
  }

  /**
   * Delete a Lighthouse report
   */
  async deleteReport(reportId: string): Promise<void> {
    const response = await apiClient.delete(`${this.baseUrl}/reports/${reportId}`);

    if (!response.success) {
      throw new Error(response.error || 'Failed to delete Lighthouse report');
    }
  }

  /**
   * Get Lighthouse service health status
   */
  async getHealthStatus(): Promise<{
    status: 'healthy' | 'unhealthy';
    details: {
      chromeAvailable: boolean;
      lighthouseVersion: string;
      timestamp: string;
    };
  }> {
    const response = await apiClient.get<{
      status: 'healthy' | 'unhealthy';
      details: {
        chromeAvailable: boolean;
        lighthouseVersion: string;
        timestamp: string;
      };
    }>(`${this.baseUrl}/health`, {
      skipAuth: true,
      cache: true,
      cacheTTL: 30 * 1000 // Cache for 30 seconds
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to check Lighthouse service health');
    }

    return response.data;
  }

  /**
   * Get Lighthouse analysis history for a specific URL
   */
  async getUrlHistory(url: string, options: {
    limit?: number;
    days?: number;
  } = {}): Promise<LighthouseReport[]> {
    const response = await apiClient.get<LighthouseReport[]>(
      `${this.baseUrl}/history`,
      {
        params: {
          url,
          limit: options.limit || 10,
          days: options.days || 30
        },
        cache: true,
        cacheTTL: 5 * 60 * 1000 // Cache for 5 minutes
      }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch URL analysis history');
    }

    return response.data;
  }

  /**
   * Compare two Lighthouse reports
   */
  async compareReports(reportId1: string, reportId2: string): Promise<{
    report1: LighthouseReport;
    report2: LighthouseReport;
    comparison: {
      performance_diff: number;
      accessibility_diff: number;
      best_practices_diff: number;
      seo_diff: number;
      lcp_diff: number;
      fid_diff: number;
      cls_diff: number;
    };
  }> {
    const response = await apiClient.get<{
      report1: LighthouseReport;
      report2: LighthouseReport;
      comparison: {
        performance_diff: number;
        accessibility_diff: number;
        best_practices_diff: number;
        seo_diff: number;
        lcp_diff: number;
        fid_diff: number;
        cls_diff: number;
      };
    }>(`${this.baseUrl}/compare`, {
      params: {
        report1: reportId1,
        report2: reportId2
      },
      cache: true,
      cacheTTL: 10 * 60 * 1000 // Cache for 10 minutes
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to compare Lighthouse reports');
    }

    return response.data;
  }

  /**
   * Schedule recurring Lighthouse analysis
   */
  async scheduleAnalysis(options: {
    url: string;
    frequency: 'daily' | 'weekly' | 'monthly';
    formFactor?: 'mobile' | 'desktop';
    categories?: string[];
    projectId?: string;
  }): Promise<{
    scheduleId: string;
    nextRun: string;
  }> {
    const response = await apiClient.post<{
      scheduleId: string;
      nextRun: string;
    }>(`${this.baseUrl}/schedule`, options);

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to schedule Lighthouse analysis');
    }

    return response.data;
  }

  /**
   * Cancel scheduled Lighthouse analysis
   */
  async cancelScheduledAnalysis(scheduleId: string): Promise<void> {
    const response = await apiClient.delete(`${this.baseUrl}/schedule/${scheduleId}`);

    if (!response.success) {
      throw new Error(response.error || 'Failed to cancel scheduled analysis');
    }
  }
}

// Export singleton instance
export const lighthouseService = new LighthouseService();
